import i18n from '@/i18n'
import { useUserStore } from '@/manage/stores/user'
import { appStorage } from '@/utils/storage/app'
import { usePostHog } from '@/shared/composables/usePostHog'

const { posthog } = usePostHog()

const DEFAULT_ACCEPT_HEADER = 'text/event-stream, application/json'

export interface StreamConsumeContext {
  markStreamingStarted: () => void
}

export interface AuthenticatedStreamRequestOptions<TBody, TResult> {
  baseURL: string
  endpoint: string
  body: TBody
  signal?: AbortSignal
  authReason: string
  accept?: string
  headers?: Record<string, string>
  maxRetry?: number
  retryDelayMs?: number
  transientStatuses?: Set<number>
  shouldRetryError?: (error: unknown) => boolean
  consumeResponse: (response: Response, context: StreamConsumeContext) => Promise<TResult>
}

const getLangHeader = () => {
  const currentLocale = i18n.global.locale.value || appStorage.getLocale()
  return String(currentLocale).startsWith('en') ? 'en' : 'zh'
}

export const extractResponseErrorDetail = async (response: Response) => {
  try {
    const cloned = response.clone()
    const contentType = cloned.headers.get('content-type') || ''

    if (contentType.includes('application/json')) {
      const payload = (await cloned.json()) as {
        detail?: unknown
        message?: unknown
        msg?: unknown
      }
      const detail = payload?.detail ?? payload?.message ?? payload?.msg
      if (typeof detail === 'string' && detail.trim()) return detail.trim()
      if (Array.isArray(detail) && detail.length > 0) {
        const text = detail
          .map((item) => {
            if (typeof item === 'string') return item.trim()
            if (!item || typeof item !== 'object') return ''
            const entry = item as {
              loc?: unknown
              msg?: unknown
              message?: unknown
              type?: unknown
            }
            const message =
              typeof entry.msg === 'string' && entry.msg.trim()
                ? entry.msg.trim()
                : typeof entry.message === 'string' && entry.message.trim()
                  ? entry.message.trim()
                  : ''
            const loc = Array.isArray(entry.loc)
              ? entry.loc
                  .map((part) => String(part || '').trim())
                  .filter(Boolean)
                  .join('.')
              : ''
            if (loc && message) return `${loc}: ${message}`
            if (message) return message
            if (loc) return loc
            return typeof entry.type === 'string' ? entry.type.trim() : ''
          })
          .filter(Boolean)
          .join('; ')
          .trim()
        if (text) return text
      }
      if (detail && typeof detail === 'object') {
        const entry = detail as { msg?: unknown; message?: unknown }
        if (typeof entry.msg === 'string' && entry.msg.trim()) return entry.msg.trim()
        if (typeof entry.message === 'string' && entry.message.trim()) return entry.message.trim()
      }
    }

    const text = (await cloned.text()).trim()
    if (text) return text
  } catch (error) {
    console.error('[StreamRequest] 提取响应错误详情失败:', error)
    posthog.capture('api_request_error', {
      module: 'streamRequest',
      operation: 'extract_error_detail',
      error: String(error),
    })
    return ''
  }

  return ''
}

export const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export const isAbortLikeError = (error: unknown) =>
  error instanceof DOMException
    ? error.name === 'AbortError'
    : typeof error === 'object' &&
        error !== null &&
        'name' in error &&
        (error as { name?: unknown }).name === 'AbortError'

export const getErrorMessage = (error: unknown) => {
  if (typeof error === 'object' && error && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string') return message.toLowerCase()
  }
  return ''
}

export const requestAuthenticatedStream = async <TBody, TResult>(
  options: AuthenticatedStreamRequestOptions<TBody, TResult>,
): Promise<TResult> => {
  const userStore = useUserStore()
  const maxRetry = options.maxRetry ?? 0
  const retryDelayMs = options.retryDelayMs ?? 0
  let attempt = 0

  while (true) {
    const token = await userStore.getFreshToken(undefined, {
      allowStaleOnNetworkError: true,
      reason: options.authReason,
    })

    let startedStreaming = false

    try {
      const response = await fetch(`${options.baseURL}${options.endpoint}`, {
        method: 'POST',
        signal: options.signal,
        headers: {
          'Content-Type': 'application/json',
          Accept: options.accept || DEFAULT_ACCEPT_HEADER,
          'x-easyhire-lang': getLangHeader(),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(userStore.isAdminModeActive ? { 'X-Admin-Mode': 'true' } : {}),
          ...options.headers,
        },
        body: JSON.stringify(options.body),
      })

      if (response.status === 401 && attempt < maxRetry) {
        const refreshedToken = await userStore.forceRefreshDefaultToken()
        if (refreshedToken) {
          attempt += 1
          continue
        }
      }

      if (!response.ok) {
        const detail = await extractResponseErrorDetail(response)
        const statusError = new Error(detail || `Stream request failed with status ${response.status}`)
        const shouldRetry =
          !startedStreaming &&
          Boolean(options.transientStatuses?.has(response.status)) &&
          attempt < maxRetry

        if (shouldRetry) {
          attempt += 1
          await delay(retryDelayMs * attempt)
          continue
        }

        throw statusError
      }

      return await options.consumeResponse(response, {
        markStreamingStarted: () => {
          startedStreaming = true
        },
      })
    } catch (error) {
      const shouldRetry =
        !startedStreaming &&
        Boolean(options.shouldRetryError?.(error)) &&
        attempt < maxRetry

      if (shouldRetry) {
        attempt += 1
        await delay(retryDelayMs * attempt)
        continue
      }

      throw error
    }
  }
}
